/**
 * SearchSpring Filters Module
 *
 * Renders facet filters matching Horizon's accordion pattern.
 * Supports list facets, color swatch facets, and price range facets.
 * Active filter pills with individual clear + clear-all.
 *
 * Loaded as standalone script by ss-collection.liquid.
 * Depends on: window.SSURLState, window.__ssCollection
 */

(function () {
  'use strict';

  // SVG icons (inline to avoid asset dependencies)
  const CARET_SVG =
    '<span class="svg-wrapper icon-caret icon-animated">' +
    '<svg viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M1 1L5 5L9 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg></span>';

  const CLOSE_SVG =
    '<span class="svg-wrapper svg-wrapper--smaller">' +
    '<svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<path d="M4 4L10 10M10 4L4 10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
    '</svg></span>';

  const CHECKMARK_SVG =
    '<span class="svg-wrapper icon-checkmark sorting-filter__checkmark">' +
    '<svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M3 8L6.5 11.5L13 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg></span>';

  const FILTER_SVG =
    '<svg width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M16.823 6h-12m10 4h-8m2 4h4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
    '</svg>';

  // =========================================================================
  // Color mapping
  // =========================================================================

  let colorMap = null;

  function getColorMap() {
    if (colorMap !== null) return colorMap;
    colorMap = {};

    const el = document.getElementById('ss-color-map');
    if (el) {
      try {
        colorMap = JSON.parse(el.textContent);
      } catch {
        // ignore
      }
    }
    return colorMap;
  }

  function getColorValue(colorName) {
    const map = getColorMap();
    const normalized = colorName.toLowerCase().trim();
    return map[normalized] || map[colorName] || null;
  }

  // =========================================================================
  // Escaping
  // =========================================================================

  // Athos returns HTML-encoded text; decode before re-escaping.
  function decodeEntities(str) {
    const txt = document.createElement('textarea');
    txt.innerHTML = String(str);
    return txt.value;
  }

  function escapeHtml(str) {
    if (!str) return '';
    return decodeEntities(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function escapeAttr(str) {
    if (!str) return '';
    return decodeEntities(str).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  }

  // =========================================================================
  // SSFilters
  // =========================================================================

  class SSFilters {
    #container;
    #filtersEl;
    #sortContainer;
    #activeFiltersEl;
    #priceDebounceTimer = null;
    #hasRenderedFacets = false;
    #userInteracting = false;

    constructor(containerEl) {
      this.#container = containerEl;
      if (!this.#container) return;

      // Drawer-only layout: no horizontal filter bar, toggle visible on all breakpoints
      this.#container.innerHTML =
        '<div class="facets-toggle ss-facets-toggle facets-toggle--drawer">' +
        '<div class="facets-toggle__wrapper">' +
        '<button type="button" class="button facets-toggle__button button-unstyled button-unstyled--with-icon ss-mobile-filter-trigger">' +
        '<span class="svg-wrapper">' + FILTER_SVG + '</span>' +
        'Filter/Sort' +
        '</button>' +
        '</div>' +
        '</div>';

      this.#filtersEl = this.#container.querySelector('.ss-facets__filters');
      this.#sortContainer = this.#container.querySelector('.ss-facets__sort-container');
      this.#activeFiltersEl = this.#container.querySelector('.ss-facets__active-filters');


      this.#container.addEventListener('click', this.#handleClick.bind(this));
      this.#container.addEventListener('change', this.#handleChange.bind(this));
      this.#container.addEventListener('input', this.#handlePriceInput.bind(this));
    }

    get sortContainer() {
      return this.#sortContainer;
    }

    render(response) {
      if (!this.#container) return;

      const { facets, filterSummary, pagination } = response;

      if (this.#userInteracting) {
        // User clicked a filter — don't touch facet DOM (keeps panel open).
        // Just update the active filter pills and product count.
        this.#userInteracting = false;
      } else {
        // Initial load, popstate, or external trigger — full facet render.
        this.#renderFacets(facets || []);
        this.#hasRenderedFacets = true;
      }

      this.#renderActiveFilters(filterSummary || []);
    }

    // -----------------------------------------------------------------------
    // Facet rendering
    // -----------------------------------------------------------------------

    #renderFacets(facets) {
      if (!this.#filtersEl) return;

      const html = facets
        .filter((facet) => facet.field !== 'collection_handle')
        .map((facet) => {
          if (facet.type === 'range' || facet.field === 'ss_price') {
            return this.#renderPriceRangeFacet(facet);
          }
          if (this.#isColorFacet(facet)) {
            return this.#renderSwatchFacet(facet);
          }
          return this.#renderListFacet(facet);
        })
        .join('');

      this.#filtersEl.innerHTML = html;
    }

    #isColorFacet(facet) {
      return /color|colour/i.test(facet.field) || /color|colour/i.test(facet.label);
    }

    #renderListFacet(facet) {
      const activeCount = (facet.values || []).filter((v) => v.active).length;
      const shouldUsePills = facet.values && facet.values.every((v) => (v.label || v.value || '').length <= 3);
      const statusHTML = this.#renderFacetStatus(activeCount, facet);

      const items = (facet.values || [])
        .map((val, i) => {
          const isDisabled = val.count === 0 && !val.active;
          const inputId = 'SSFilter-' + escapeAttr(facet.field) + '-' + i;
          const disabledClass = isDisabled ? ' facets__inputs-list-item--disabled' : '';

          if (shouldUsePills) {
            return (
              '<li class="facets__inputs-list-item' + disabledClass + '">' +
              '<div class="facets__pill-wrapper">' +
              '<input type="checkbox" class="facets__pill-input"' +
              ' name="filter.' + escapeAttr(facet.field) + '"' +
              ' value="' + escapeAttr(val.value) + '"' +
              ' id="' + inputId + '"' +
              ' data-label="' + escapeAttr(val.label || val.value) + '"' +
              (val.active ? ' checked' : '') +
              (isDisabled ? ' disabled' : '') +
              '>' +
              '<label class="facets__pill-label" for="' + inputId + '">' +
              escapeHtml(val.label || val.value) +
              '</label>' +
              '</div></li>'
            );
          }

          return (
            '<li class="facets__inputs-list-item' + disabledClass + '">' +
            '<label class="checkbox' + (isDisabled ? ' checkbox--disabled' : '') + '">' +
            '<input type="checkbox" class="checkbox__input"' +
            ' name="filter.' + escapeAttr(facet.field) + '"' +
            ' value="' + escapeAttr(val.value) + '"' +
            ' id="' + inputId + '"' +
            ' data-label="' + escapeAttr(val.label || val.value) + '"' +
            (val.active ? ' checked' : '') +
            (isDisabled ? ' disabled' : '') +
            '>' +
            '<span class="checkbox__label">' +
            '<span class="icon-checkmark"><svg viewBox="0 0 16 16" fill="none"><path d="M3 8L6.5 11.5L13 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></span>' +
            '<span class="checkbox__label-text">' + escapeHtml(val.label || val.value) +
            '</span></span>' +
            '</label></li>'
          );
        })
        .join('');

      const listClass = shouldUsePills
        ? 'facets__inputs-list facets__inputs-list--grid list-unstyled'
        : 'facets__inputs-list list-unstyled';

      const hasActive = (facet.values || []).some((v) => v.active);
      return this.#wrapInAccordion(
        facet.label || facet.field,
        statusHTML,
        '<ul class="' + listClass + '">' + items + '</ul>',
        facet.field,
        hasActive
      );
    }

    #renderSwatchFacet(facet) {
      const activeCount = (facet.values || []).filter((v) => v.active).length;
      const statusHTML = this.#renderSwatchStatus(activeCount, facet);

      const items = (facet.values || [])
        .map((val, i) => {
          const isDisabled = val.count === 0 && !val.active;
          const colorValue = getColorValue(val.label || val.value);
          const inputId = 'SSFilter-' + escapeAttr(facet.field) + '-' + i;
          const disabledClass = isDisabled ? ' facets__inputs-list-item--disabled' : '';

          let swatchStyle = '--swatch-background: #ccc;';
          if (colorValue) {
            if (colorValue.startsWith('http') || colorValue.startsWith('/')) {
              swatchStyle = '--swatch-background: url(' + escapeAttr(colorValue) + ');';
            } else {
              swatchStyle = '--swatch-background: ' + escapeAttr(colorValue) + ';';
            }
          }

          return (
            '<li class="facets__inputs-list-item' + disabledClass + '">' +
            '<fieldset class="variant-option variant-option--buttons variant-option--swatches' +
            (isDisabled ? ' variant-option--swatches-disabled' : '') + '"' +
            ' aria-label="' + escapeAttr(val.label || val.value) + '">' +
            '<label class="variant-option__button-label variant-option__button-label--has-swatch">' +
            '<div class="variant-option__swatch-wrapper">' +
            '<input tabindex="0" type="checkbox"' +
            ' name="filter.' + escapeAttr(facet.field) + '"' +
            ' value="' + escapeAttr(val.value) + '"' +
            ' id="' + inputId + '"' +
            ' aria-label="' + escapeAttr(val.label || val.value) + '"' +
            (val.active ? ' checked' : '') +
            (isDisabled ? ' disabled' : '') +
            '>' +
            '<span class="swatch swatch--filter" style="' + swatchStyle + '"></span>' +
            '</div>' +
            '<label class="facets__swatch-label" for="' + inputId + '">' +
            escapeHtml(val.label || val.value) +
            '</label>' +
            '</label>' +
            '</fieldset></li>'
          );
        })
        .join('');

      // Calculate swatch columns same as Horizon
      let swatchColumns = (facet.values || []).length;
      if (swatchColumns > 4) {
        swatchColumns = 4;
        const mod4 = (facet.values || []).length % 4;
        const mod3 = (facet.values || []).length % 3;
        if (mod4 !== 0 && mod4 !== 3) {
          if (mod3 === 0 || mod3 === 2) {
            swatchColumns = 3;
          }
        }
      }

      const hasActive = (facet.values || []).some((v) => v.active);
      return this.#wrapInAccordion(
        facet.label || facet.field,
        statusHTML,
        '<ul class="facets__inputs-list facets__inputs-list--swatches facets__inputs-list--swatches-grid list-unstyled" style="--swatch-columns: ' + swatchColumns + ';">' +
        items + '</ul>',
        facet.field,
        hasActive
      );
    }

    #renderPriceRangeFacet(facet) {
      const activeMin = facet.active ? facet.active.low : '';
      const activeMax = facet.active ? facet.active.high : '';
      const rangeMax = facet.range ? facet.range.high : '';
      const hasActive = activeMin || activeMax;
      const statusText = hasActive
        ? '$' + (activeMin || '0') + ' – $' + (activeMax || rangeMax)
        : '';

      const statusHTML = statusText
        ? '<span class="hide-when-empty">' + escapeHtml(statusText) + '</span>'
        : '';

      const content =
        '<div class="price-facet">' +
        '<div class="facets__inputs-wrapper facets__inputs-wrapper--row">' +
        '<div class="price-facet__inputs-wrapper">' +
        '<div class="field price-facet__field">' +
        '<input class="field__input price-facet__input" type="text" inputmode="decimal"' +
        ' name="filter.ss_price.low" placeholder="0"' +
        ' data-price-range="min"' +
        (activeMin ? ' value="' + escapeAttr(String(activeMin)) + '"' : '') +
        ' autocomplete="off">' +
        '<label class="field__label price-facet__label">$</label>' +
        '</div>' +
        '<div class="price-facet__separator">to</div>' +
        '<div class="field price-facet__field">' +
        '<input class="field__input price-facet__input" type="text" inputmode="decimal"' +
        ' name="filter.ss_price.high" placeholder="' + escapeAttr(String(rangeMax)) + '"' +
        ' data-price-range="max"' +
        (activeMax ? ' value="' + escapeAttr(String(activeMax)) + '"' : '') +
        ' autocomplete="off">' +
        '<label class="field__label price-facet__label">$</label>' +
        '</div>' +
        '</div></div>' +
        (rangeMax ? '<div class="price-facet__highest-price">The highest price is $' + escapeHtml(String(rangeMax)) + '</div>' : '') +
        '</div>';

      return this.#wrapInAccordion(
        facet.label || 'Price',
        statusHTML,
        content,
        facet.field || 'ss_price',
        hasActive
      );
    }

    #renderFacetStatus(activeCount, facet) {
      if (activeCount === 0) return '';
      if (activeCount === 1) {
        const activeVal = (facet.values || []).find((v) => v.active);
        return '<span class="hide-when-empty">' + escapeHtml(activeVal ? (activeVal.label || activeVal.value) : String(activeCount)) + '</span>';
      }
      return '<span class="bubble facets__bubble hide-when-empty">' + activeCount + '</span>';
    }

    #renderSwatchStatus(activeCount, facet) {
      if (activeCount === 0) return '';
      if (activeCount > 3) {
        return '<span class="bubble facets__bubble">' + activeCount + '</span>';
      }
      // Show mini swatches for 1-3 active values
      const activeVals = (facet.values || []).filter((v) => v.active);
      const swatches = activeVals
        .map((val) => {
          const colorValue = getColorValue(val.label || val.value);
          let style = '--swatch-background: #ccc;';
          if (colorValue) {
            if (colorValue.startsWith('http') || colorValue.startsWith('/')) {
              style = '--swatch-background: url(' + escapeAttr(colorValue) + ');';
            } else {
              style = '--swatch-background: ' + escapeAttr(colorValue) + ';';
            }
          }
          return '<span class="swatch swatch--filter" style="' + style + '"></span>';
        })
        .join('');
      return '<span class="facets__swatch-wrapper">' + swatches + '</span>';
    }

    #wrapInAccordion(label, statusHTML, content, field, hasActive) {
      const paramName = escapeAttr(field.replace(/\./g, '-'));
      const clearActiveClass = hasActive ? ' facets__clear--active' : '';

      return (
        '<accordion-custom class="facets__item"' +
        ' data-disable-animation-on-desktop="true"' +
        ' data-close-with-escape="true"' +
        ' data-filter-param-name="filter-' + paramName + '">' +
        '<details class="facets__panel" data-auto-close-details="desktop">' +
        '<summary class="facets__summary">' +
        '<span class="facets__label">' + escapeHtml(label) + '</span>' +
        '<div class="facets__status-wrapper">' +
        '<facet-status-component class="facets__status">' + statusHTML + '</facet-status-component>' +
        CARET_SVG +
        '</div>' +
        '</summary>' +
        '<floating-panel-component data-close-on-resize class="facets__inputs facets__panel-content details-content">' +
        '<div class="facets__inputs-wrapper">' + content + '</div>' +
        '<div class="facets__clear' + clearActiveClass + '">' +
        '<button type="button" class="clear-filter button button-secondary ss-clear-facet" data-facet-field="' + escapeAttr(field) + '">Clear</button>' +
        '</div>' +
        '</floating-panel-component>' +
        '</details>' +
        '</accordion-custom>'
      );
    }

    // -----------------------------------------------------------------------
    // Active filter pills
    // -----------------------------------------------------------------------

    #renderActiveFilters(filterSummary) {
      if (!this.#activeFiltersEl) return;

      if (!filterSummary || filterSummary.length === 0) {
        this.#activeFiltersEl.innerHTML = '';
        return;
      }

      const pills = filterSummary
        .map(
          (f) =>
            '<facet-remove-component class="pills__pill pills__pill--desktop-small facets-remove__pill"' +
            ' tabindex="0" role="button"' +
            ' data-filter-field="' + escapeAttr(f.field) + '"' +
            ' data-filter-value="' + escapeAttr(f.value) + '">' +
            escapeHtml((f.filterLabel || f.field) + ': ' + (f.label || f.value)) +
            CLOSE_SVG +
            '<span class="visually-hidden">Remove</span>' +
            '</facet-remove-component>'
        )
        .join('');

      const clearAll =
        '<button type="button" class="button-unstyled facets__clear-all-link facets__clear-all-link--active ss-clear-all">' +
        'Clear all</button>';

      this.#activeFiltersEl.innerHTML = pills + clearAll;
    }

    // -----------------------------------------------------------------------
    // Count
    // -----------------------------------------------------------------------


    // -----------------------------------------------------------------------
    // Event handlers
    // -----------------------------------------------------------------------

    #handleClick(e) {
      // Remove individual filter pill
      const pill = e.target.closest('facet-remove-component[data-filter-field]');
      if (pill) {
        e.preventDefault();
        const field = pill.dataset.filterField;
        const value = pill.dataset.filterValue;

        // Uncheck the corresponding checkbox
        const input = this.#container.querySelector(
          'input[name="filter.' + CSS.escape(field) + '"][value="' + CSS.escape(value) + '"]'
        );
        if (input) input.checked = false;

        this.#applyFilters();
        this.#dispatchFilterEvent(field, value, 'remove');
        return;
      }

      // Clear single facet
      const clearFacet = e.target.closest('.ss-clear-facet');
      if (clearFacet) {
        e.preventDefault();
        const field = clearFacet.dataset.facetField;
        const accordion = clearFacet.closest('accordion-custom');
        if (accordion) {
          accordion.querySelectorAll('input[type="checkbox"]:checked').forEach((input) => {
            input.checked = false;
          });
          accordion.querySelectorAll('input[data-price-range]').forEach((input) => {
            input.value = '';
          });
          // Update status and clear button visibility
          const statusEl = accordion.querySelector('facet-status-component');
          if (statusEl) statusEl.innerHTML = '';
          const clearDiv = clearFacet.closest('.facets__clear');
          if (clearDiv) clearDiv.classList.remove('facets__clear--active');
        }
        this.#userInteracting = true;
        this.#applyFilters();
        this.#dispatchFilterEvent(field, '', 'clear_all');
        return;
      }

      // Clear all
      const clearAll = e.target.closest('.ss-clear-all');
      if (clearAll) {
        e.preventDefault();
        this.#clearAllFilters();
        this.#dispatchFilterEvent('', '', 'clear_all');
        return;
      }
    }

    #handleChange(e) {
      const input = e.target;
      if (!input.name || !input.name.startsWith('filter.')) return;
      if (input.dataset.priceRange) return; // handled by #handlePriceInput

      const action = input.checked ? 'add' : 'remove';
      this.#userInteracting = true;
      this.#updateFacetStatus(input);
      this.#applyFilters();
      this.#dispatchFilterEvent(input.name, input.value, action);
    }

    #handlePriceInput(e) {
      const input = e.target;
      if (!input.dataset.priceRange) return;

      clearTimeout(this.#priceDebounceTimer);
      this.#priceDebounceTimer = setTimeout(() => {
        this.#userInteracting = true;
        this.#applyFilters();
        this.#dispatchFilterEvent('ss_price', input.value, 'add');
      }, 300);
    }

    /**
     * Update the facet status badge in the accordion summary after a checkbox change.
     * Mirrors Horizon's FacetStatusComponent.updateListSummary / #updateBubbleSummary.
     */
    #updateFacetStatus(input) {
      const accordion = input.closest('accordion-custom');
      if (!accordion) return;

      const statusEl = accordion.querySelector('facet-status-component');
      if (!statusEl) return;

      // Find all checked inputs within this accordion's facet
      const checkedInputs = accordion.querySelectorAll('input[type="checkbox"]:checked');
      const count = checkedInputs.length;

      const isSwatchFacet = !!accordion.querySelector('.facets__inputs-list--swatches');

      // Toggle clear button visibility
      const clearDiv = accordion.querySelector('.facets__clear');
      if (clearDiv) {
        clearDiv.classList.toggle('facets__clear--active', count > 0);
      }

      if (count === 0) {
        statusEl.innerHTML = '';
        return;
      }

      if (isSwatchFacet) {
        // Swatch facets: show mini swatches for 1-3, bubble count for 4+
        if (count > 3) {
          statusEl.innerHTML = '<span class="bubble facets__bubble">' + count + '</span>';
        } else {
          const swatchHtml = Array.from(checkedInputs)
            .map((inp) => {
              const swatch = inp.parentElement?.querySelector('span.swatch');
              return swatch ? swatch.outerHTML : '';
            })
            .join('');
          statusEl.innerHTML = '<span class="facets__swatch-wrapper">' + swatchHtml + '</span>';
        }
      } else {
        // List facets: show label for 1 selected, bubble count for 2+
        if (count === 1) {
          const label = checkedInputs[0].dataset?.label || checkedInputs[0].closest('label')?.textContent?.trim() || '';
          statusEl.innerHTML = '<span class="hide-when-empty">' + escapeHtml(label) + '</span>';
        } else {
          statusEl.innerHTML = '<span class="bubble facets__bubble hide-when-empty">' + count + '</span>';
        }
      }
    }

    #applyFilters() {
      const params = this.#buildParamsFromDOM();
      window.SSURLState.writeToURL(params);
      (window.__ssCollection || window.__ssSearch).refresh(params);
    }

    #clearAllFilters() {
      // Uncheck all filter inputs
      this.#container.querySelectorAll('input[name^="filter."]').forEach((input) => {
        if (input.type === 'checkbox') input.checked = false;
        if (input.dataset.priceRange) input.value = '';
      });

      // Get current sort params only (clear filters, keep sort)
      const params = this.#buildParamsFromDOM();
      window.SSURLState.writeToURL(params);
      (window.__ssCollection || window.__ssSearch).refresh(params);
    }

    #buildParamsFromDOM() {
      const params = {};

      // Collect checked checkboxes
      this.#container.querySelectorAll('input[type="checkbox"][name^="filter."]:checked').forEach((input) => {
        const name = input.name;
        if (params[name] !== undefined) {
          if (Array.isArray(params[name])) {
            params[name].push(input.value);
          } else {
            params[name] = [params[name], input.value];
          }
        } else {
          params[name] = input.value;
        }
      });

      // Collect price range
      const minInput = this.#container.querySelector('input[data-price-range="min"]');
      const maxInput = this.#container.querySelector('input[data-price-range="max"]');
      if (minInput && minInput.value) params['filter.ss_price.low'] = minInput.value;
      if (maxInput && maxInput.value) params['filter.ss_price.high'] = maxInput.value;

      // Preserve sort params from URL (sorting module manages them)
      const urlParams = window.SSURLState.readFromURL();
      for (const [key, value] of Object.entries(urlParams)) {
        if (key.startsWith('sort.')) {
          params[key] = value;
        }
      }

      return params;
    }

    #dispatchFilterEvent(field, value, action) {
      document.dispatchEvent(
        new CustomEvent('ss:filter-changed', {
          detail: { field, value, action },
        })
      );
    }
  }

  window.SSFilters = SSFilters;
})();
